import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { GlobalContext } from "context/Provider";
import Session from "helpers/session";
import API from "api";
import { useNavigate } from "react-router";
import { Grid } from "@mui/material";
import SimpleGrid, {
  CellProps,
  BodyProps
} from "components/shared/SimpleGrid/SimpleGrid";
import { StyledSimpleGridContainer } from "../List/List.styles";
import EncounterTimer from "components/shared/EncounterTimer/EncounterTimer";
import { useTranslation } from "react-i18next";
import { associateReadingType } from "helpers/variables";
import {
  uBreathing,
  uCoordinator,
  uDate,
  uDiastolic,
  uGlucose,
  uMeal,
  uNote,
  uOxygenSaturation,
  uPulse,
  uSystolic,
  uTemperature,
  uWeight,
  uActivity
} from "helpers/translationStringConstants";
import { COORDINATOR, UNITS } from "helpers/appConstants";
import { READING_TYPES } from "helpers/appConstants";
import { formatTableBody } from "./List.util";
const { heartRate } = READING_TYPES;

interface ListProps {
  type: string;
}

interface HeadersInterface {
  "Blood Pressure": CellProps[];
  "Blood Glucose": CellProps[];
  "Heart Rate": CellProps[];
  "Oxygen Saturation": CellProps[];
  Spirometry: CellProps[];
  Temperature: CellProps[];
  Weight: CellProps[];  
  Activity: CellProps[];
  "Respiration Rate": CellProps[];
  "Member Note": CellProps[];
}

export default function List(props: ListProps) {
  const { t } = useTranslation();
  const memberID = window.location.pathname.split("/").pop();
  const [readingList, setReadingList] = useState<BodyProps[]>([]);
  const { config, readingsDispatch } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [totalReadingList, setTotalReadingList] = useState<number>(0);
  const heartRateToggle = props.type !== heartRate.display;

  const paginationAction = (itemsPerPage?: number, currentPage?: number) => {
    const additionalSources = heartRateToggle ? "" : "&type=spo2&type=bp";

    API.kore.readings
      .getForOne(
        memberID || "",
        Session.crackCookieToken("coordinator"),
        associateReadingType(props.type),
        `${additionalSources}&limit=${itemsPerPage}&page=${currentPage}&order=desc&sort_by=measured_at`
      )
      .then((readings: any) => {
        setReadingList(
          formatTableBody(
            readings.items,
            selectReading,
            config?.basicOptions.units,
            props.type
          )
        );
      });
  };

  const headers: HeadersInterface = {
    "Blood Pressure": [
      { content: t(uDate), align: "center" },
      {
        content: `${t(uSystolic)}, ${UNITS.bloodPressure.mmHg}`,
        align: "center"
      },
      {
        content: `${t(uDiastolic)}, ${UNITS.bloodPressure.mmHg}`,
        align: "center"
      },
      { content: `${t(uPulse)}, ${UNITS.heartRate.bpm}`, align: "center" }
    ],
    "Blood Glucose": [
      { content: t(uDate), align: "center" },
      { content: `${t(uGlucose)}, ${UNITS.glucose.mg}`, align: "center" },
      { content: t(uMeal), align: "center" }
    ],
    "Heart Rate": [
      { content: t(uDate), align: "center" },
      { content: `${t(uPulse)}, ${UNITS.heartRate.bpm}`, align: "center" },
      { content: "Source Device", align: "center" }
    ],
    "Oxygen Saturation": [
      { content: t(uDate), align: "center" },
      { content: t(uOxygenSaturation), align: "center" }
    ],
    Spirometry: [
      { content: t(uDate), align: "center" },
      { content: "FVC,L", align: "center" },
      { content: "FEV1,L", align: "center" },
      { content: "FEV1/FVC", align: "center" }
    ],
    Temperature: [
      { content: t(uDate), align: "center" },
      { content: `${t(uTemperature)}, F`, align: "center" }
    ],
    Weight: [
      { content: t(uDate), align: "center" },
      { content: `${t(uWeight)}, ${UNITS.weight.pounds}`, align: "center" }
    ],
    Activity: [
      { content: t(uDate), align: "center" },
      { content: `${t(uActivity)}, ${UNITS.activity.steps}`, align: "center" }
    ],
    "Respiration Rate": [
      { content: t(uDate), align: "center" },
      { content: t(uBreathing), align: "center" }
    ],
    "Member Note": [
      { content: t(uDate), align: "center" },
      { content: t(uCoordinator), align: "center" },
      { content: t(uNote), align: "center" }
    ]
  };

  const selectReading = (event: ChangeEvent<HTMLElement>) => {
    //TODO: utilize Mel call to avoid improper tier access on readings
    API.kore.readings
      .getSingleReading(
        event.target.dataset.id || "",
        Session.crackCookieToken(COORDINATOR)
      )
      .then((reading) => {
        //TODO: ensure all necessary data is present for reading details
        readingsDispatch({ type: "UPDATE_READING", payload: reading });
        const currentPath = window.location.pathname;
        navigate(currentPath.replace("list", "details"));
      });
  };

  useEffect(() => {
    switch (props.type) {
      case "Member Note":
        //TODO: API call for Notes
        setReadingList([]);
        break;
      case "ECG":
        //TODO: API call for ECG if necessary
        setReadingList([]);
        break;
      default:
        API.kore.readings
          .getCount(
            memberID || "",
            Session.crackCookieToken(COORDINATOR),
            associateReadingType(props.type)
          )
          .then((readings) => {
            setTotalReadingList(readings.count);
            paginationAction(10, 0);
          });
    }
  }, [props.type]);
  return (
    <Grid container>
      <Grid item xs={0.5} lg={0.7} />
      <Grid item xs={11.2} lg={11.1}>
        <StyledSimpleGridContainer>
          <EncounterTimer></EncounterTimer>
          <SimpleGrid
            headers={headers[props.type as keyof HeadersInterface]}
            body={readingList}
            paginationAction={paginationAction}
            totalList={totalReadingList}
          />
        </StyledSimpleGridContainer>
      </Grid>
    </Grid>
  );
}
